import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Row, FloatingLabel } from 'react-bootstrap';
import { useSelector, useDispatch } from "react-redux";
import useAxios from "../services/AxiosConfig";
import managementStyles from '../views/ManagementPages.module.css';
import { showAlert } from "./PerinHealthAlertSlice";
import { formatDateWithTime } from "../utils/dateUtils";

const VersionModal = ({ show, handleClose }) => {

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);

    const [currentVersionInfo, setCurrentVersionInfo] = useState('');
    const [validated, setValidated] = useState(false);
    const [recentUpdate, setRecentUpdate] = useState(false);

    const [newAppVersion, setNewAppVersion] = useState('');
    const [newPatchVersion, setNewPatchVersion] = useState('');

    const [appNotes, setAppNotes] = useState('');
    const [patchNotes, setPatchNotes] = useState('');

    const [updatingAppVersion, setUpdatingAppVersion] = useState(false);
    const [updatingPatchVersion, setUpdatingPatchVersion] = useState(false);

    const [appVersionError, setAppVersionError] = useState('');
    const [patchVersionError, setPatchVersionError] = useState('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    useEffect(() => {
        getLatestUpdate();
    }, []);

    const getLatestUpdate = () => {
        axios.get(`latestVersion`)
            .then((responseData) => {
                if (responseData.data.data) {
                    const mostRecentVersion = responseData.data.data;
                    setCurrentVersionInfo(mostRecentVersion);
                    if (mostRecentVersion.createdDate) {
                        const createdDateObj = new Date(mostRecentVersion.createdDate);
                        const now = new Date();
                        const oneHourInMillis = 60 * 60 * 1000; // 1 hour in milliseconds
                        const timeDifference = Math.abs(now.getTime() - createdDateObj.getTime());
                        const isWithinOneHour = timeDifference <= oneHourInMillis;
                        setRecentUpdate(isWithinOneHour);
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    const formRef = useRef(null);

    const compareVersions = (currentVersion, newVersion) => {
        if (!currentVersion || !newVersion) {
            return { isValid: false, message: "Version strings cannot be empty" };
        }

        const currentParts = currentVersion.split('.');
        const newParts = newVersion.split('.');

        // Validate that both versions have exactly 3 parts
        if (currentParts.length !== 3 || newParts.length !== 3) {
            return { isValid: false, message: "Version must be in format X.Y.Z (e.g., 1.2.3)" };
        }

        // Validate that all parts are numeric
        for (let i = 0; i < 3; i++) {
            if (!/^\d+$/.test(currentParts[i]) || !/^\d+$/.test(newParts[i])) {
                return { isValid: false, message: "All version parts must be numeric" };
            }
        }

        // Convert to numbers and compare
        const currentNums = currentParts.map(Number);
        const newNums = newParts.map(Number);

        if (newNums[0] > currentNums[0]) {
            return { isValid: true, message: "New version is greater" };
        } else if (newNums[0] < currentNums[0]) {
            return { isValid: false, message: "Major version must be greater than or equal to current version" };
        }

        if (newNums[1] > currentNums[1]) {
            return { isValid: true, message: "New version is greater" };
        } else if (newNums[1] < currentNums[1]) {
            return { isValid: false, message: "Minor version must be greater than or equal to current version" };
        }

        if (newNums[2] > currentNums[2]) {
            return { isValid: true, message: "New version is greater" };
        } else if (newNums[2] < currentNums[2]) {
            return { isValid: false, message: "Patch version must be greater than current version" };
        }

        // Versions are equal
        return { isValid: false, message: "New version must be greater than current version" };
    };

    const handleFormValidation = async () => {
        setAppVersionError('');
        setPatchVersionError('');

        let hasErrors = false;

        if (updatingAppVersion) {
            const appVersionCheck = compareVersions(currentVersionInfo.appVersion, newAppVersion);
            if (!appVersionCheck.isValid) {
                setAppVersionError(appVersionCheck.message);
                hasErrors = true;
            }
        }

        if (updatingPatchVersion) {
            const patchVersionCheck = compareVersions(currentVersionInfo.patchVersion, newPatchVersion);
            if (!patchVersionCheck.isValid) {
                setPatchVersionError(patchVersionCheck.message);
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setValidated(true);
            return;
        }

        // If no errors, show confirmation modal
        setShowConfirmationModal(true);
    };

    const handleSubmit = async () => {
        const data = {
            appNotes,
            patchNotes
        }
        if (updatingAppVersion) {
            data.newAppVersion = newAppVersion
        }
        if (updatingPatchVersion) {
            data.newPatchVersion = newPatchVersion
        }
        try {
            await axios.request({
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                url: 'version',
                data: JSON.stringify(data),
            });

            // Close confirmation modal and main modal
            setShowConfirmationModal(false);

            dispatch(
                showAlert({
                    header: "Success",
                    message: "Updated version successfully",
                    type: 'success',
                })
            );
            handleDataAndClose();
        } catch (error) {

            dispatch(
                showAlert({
                    header: `Error`,
                    message: 'Could not submit updated version',
                    type: 'danger',
                    dismissable: true
                })
            );
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmationModal(false);
    };

    const handleDataAndClose = () => {
        setAppNotes('');
        setPatchNotes('');
        setNewAppVersion('');
        setNewPatchVersion('');
        setUpdatingAppVersion(false);
        setUpdatingPatchVersion(false);
        handleClose();
    };

    return (
        <>
        <Modal show={show} centered size={"xl"}>
            <Modal.Header >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Modal.Title className="mb-0">Version Control</Modal.Title>
                    <div style={{ height: '24px', borderLeft: '1px solid #030303' }}></div>
                    <span>{`Last update: ${formatDateWithTime(currentVersionInfo.createdDate, userDateFormat)}`}</span>
                </div>
            </Modal.Header>
            <Modal.Body>
                {recentUpdate &&
                    <div className={managementStyles.warningMessageContainer} style={{ marginBottom: "10px" }}>
                        {"A new version has been submitted in the last hour. Please wait to send a new update."}
                    </div>
                }
                <Form style={{ height: "99%", display: "flex", flexDirection: "column", gap: "12px" }} noValidate validated={validated}>

                    <div style={{ display: "flex", alignItems: "center", gap: "30px" }}>
                        <Form.Label className="headline-6">New App Version</Form.Label>
                        <Form.Check
                            type="switch"
                            label="Update App Version"
                            disabled={recentUpdate}
                            checked={updatingAppVersion}
                            onChange={() => setUpdatingAppVersion(!updatingAppVersion)}
                        />
                    </div>
                    <Form.Group className="mb-3">

                        <FloatingLabel controlId="floatingSelect" label="Current App Version">
                            <Form.Control
                                placeholder="Current App Version"
                                style={{ width: "300px" }}
                                value={currentVersionInfo.appVersion}
                                disabled={true}
                                className={managementStyles.BorderedComponent}
                            />

                        </FloatingLabel>

                    </Form.Group>

                    {updatingAppVersion &&
                        <>
                            <Form.Group className="mb-3">

                                <FloatingLabel controlId="floatingSelect" label="Updated App Version #">
                                    <Form.Control
                                        placeholder="Updated App Version #"
                                        style={{ width: "300px" }}
                                        required
                                        value={newAppVersion}
                                        className={managementStyles.BorderedComponent}
                                        isInvalid={!!appVersionError}
                                        onChange={(e) => {
                                            setNewAppVersion(e.target.value);
                                            setAppVersionError('');
                                        }}

                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {appVersionError || "Please provide a valid app version number."}
                                    </Form.Control.Feedback>

                                </FloatingLabel>

                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    value={appNotes}
                                    id="notes"
                                    as="textarea"
                                    rows={2}
                                    className={managementStyles.BorderedComponentTextArea}
                                    onChange={e => setAppNotes(e.target.value)}
                                    placeholder="App Notes"
                                    style={{ width: "300px" }}
                                />

                            </Form.Group>
                        </>

                    }

                    <div style={{ display: "flex", alignItems: "center", gap: "30px" }}>
                        <Form.Label className="headline-6">New Patch Version</Form.Label>
                        <Form.Check
                            type="switch"
                            label="Update Patch Version"
                            checked={updatingPatchVersion}
                            disabled={recentUpdate}
                            onChange={() => setUpdatingPatchVersion(!updatingPatchVersion)}
                        />
                    </div>
                    <Form.Group className="mb-3">

                        <FloatingLabel controlId="floatingSelect" label="Current Patch Version">
                            <Form.Control
                                placeholder="Current Patch Version"
                                style={{ width: "300px" }}
                                value={currentVersionInfo.patchVersion}
                                disabled={true}
                                className={managementStyles.BorderedComponent}
                            />

                        </FloatingLabel>

                    </Form.Group>

                    {updatingPatchVersion &&
                        <>
                            <Form.Group className="mb-3">

                                <FloatingLabel controlId="floatingSelect" label="Updated Patch Version #">
                                    <Form.Control
                                        placeholder="Updated Patch Version #"
                                        style={{ width: "300px", border: patchVersionError ? "1px solid red" : "" }}
                                        value={newPatchVersion}
                                        required
                                        className={managementStyles.BorderedComponent}
                                        isInvalid={!!patchVersionError}
                                        onChange={(e) => {
                                            setNewPatchVersion(e.target.value);
                                            setPatchVersionError('');
                                        }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {patchVersionError || "Please provide a valid patch version number."}
                                    </Form.Control.Feedback>

                                </FloatingLabel>

                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    value={patchNotes}
                                    id="notes"
                                    as="textarea"
                                    rows={2}
                                    className={managementStyles.BorderedComponentTextArea}
                                    onChange={e => setPatchNotes(e.target.value)}
                                    placeholder="Patch Notes"
                                    style={{ width: "300px" }}
                                />

                            </Form.Group>
                        </>

                    }

                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className={managementStyles.BackButton}
                    variant="outlined"
                    onClick={handleDataAndClose}
                >
                    Cancel
                </Button>
                <Button
                    className={managementStyles.CreateButton}
                    variant="secondary"
                    disabled={(!updatingAppVersion && !updatingPatchVersion) || recentUpdate}
                    onClick={handleFormValidation}
                >
                    Submit
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Confirmation Modal */}
        <Modal show={showConfirmationModal} onHide={handleCancelSubmit} centered backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Confirm Version Update</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Are you sure you want to update the version?</p>
                {updatingAppVersion && (
                    <div style={{ marginBottom: '10px' }}>
                        <strong>App Version:</strong> {currentVersionInfo.appVersion} → {newAppVersion}
                    </div>
                )}
                {updatingPatchVersion && (
                    <div style={{ marginBottom: '10px' }}>
                        <strong>Patch Version:</strong> {currentVersionInfo.patchVersion} → {newPatchVersion}
                    </div>
                )}
                {(appNotes || patchNotes) && (
                    <div style={{ marginTop: '15px' }}>
                        <strong>Notes:</strong>
                        {appNotes && <div>App: {appNotes}</div>}
                        {patchNotes && <div>Patch: {patchNotes}</div>}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className={managementStyles.BackButton}
                    variant="outlined"
                    onClick={handleCancelSubmit}
                >
                    Cancel
                </Button>
                <Button
                    className={managementStyles.CreateButton}
                    variant="secondary"
                    onClick={handleSubmit}
                >
                    Confirm Update
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default VersionModal;
